import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import MessageList from '../components/MessageList';
import { useLocation } from 'react-router-dom';
import SidebarNav from '../components/SidebarNav';

const MessagesPage = () => {
    const { t } = useTranslation();
    const location = useLocation();

    // --- State Variables ---
    const [messages, setMessages] = useState([]);
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [error, setError] = useState(null);

    // --- Theming ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";

    // --- Fetching Logic ---
    useEffect(() => {
        const fetchMessagesData = async () => {
            setLoadingMessages(true);
            try {
                const messagesRes = await axios.get('/api/messages');
                if (messagesRes.data && messagesRes.data.success) {
                    setMessages(messagesRes.data.data);
                } else {
                    throw new Error(messagesRes.data.message || 'Failed to fetch messages');
                }
            } catch (err) {
                setError(`Error fetching messages: ${err.message}`);
                console.error("Message Fetch Error:", err);
            } finally {
                setLoadingMessages(false);
            }
        };

        fetchMessagesData();
    }, []);

    // --- Intersection Observer Animations ---
    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* Sidebar */}
            <SidebarNav currentPath={location.pathname} />

            {/* Main Content Area */}
            <div className="ml-64">
                {/* Hero Section */}
                <section
                    ref={heroRef}
                    className="pt-36 pb-16 bg-black"
                >
                    <div className="container mx-auto px-6">
                        <div
                            className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                        >
                            <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${textPrimary}`}>
                                {t('developerPage.messagesTitle', 'Contact Messages')}
                            </h1>
                            <p className={`text-lg md:text-xl ${textSecondary} mb-8 leading-relaxed`}>
                                {t('developerPage.messagesSubtitle', 'View and manage messages received through the contact form.')}
                            </p>
                            <div className="mt-3 w-40 h-1 mx-auto bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section
                    ref={contentRef}
                    className={`py-8 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                >
                    <div className="container mx-auto px-6 space-y-8">
                        {/* Display Error if any */}
                        {error && (
                            <div className="max-w-5xl mx-auto bg-red-900/20 border border-red-700/30 text-red-300 px-5 py-4 rounded-lg mb-6">
                                <div className="flex items-center space-x-3 mb-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="font-bold text-sm">{t('developerPage.errorLoadingData', 'Error Loading Data')}</h3>
                                </div>
                                <pre className="text-xs whitespace-pre-wrap">{error}</pre>
                            </div>
                        )}

                        {/* Messages Content Area */}
                        <div className="max-w-5xl mx-auto bg-gray-900/60 rounded-lg border border-gray-800/50 overflow-hidden shadow-2xl backdrop-blur-sm">
                            <div className="p-6">
                                <h2 className={`text-2xl font-medium mb-6 ${textPrimary} flex items-center`}>
                                    <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                                    </svg>
                                    {t('developerPage.messagesListTitle', 'Received Messages')}
                                </h2>

                                {loadingMessages ? (
                                    <div className="py-8">
                                        <div className="animate-pulse flex flex-col space-y-4">
                                            <div className="h-10 bg-gray-800/70 rounded-md w-full"></div>
                                            <div className="h-10 bg-gray-800/70 rounded-md w-full"></div>
                                            <div className="h-10 bg-gray-800/70 rounded-md w-full"></div>
                                        </div>
                                    </div>
                                ) : messages && messages.length > 0 ? (
                                    <MessageList messages={messages} />
                                ) : (
                                    <div className="py-8 text-center">
                                        <p className={`${textSecondary} mb-2`}>{t('developerPage.noMessagesFound', 'No messages found.')}</p>
                                        <p className="text-sm text-gray-500">Messages from your contact form will appear here.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default MessagesPage; 